<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999]" @click.self="closeModal">
    <div class="w-full max-w-2xl transition-all transform bg-white rounded-lg shadow-xl">
      <div class="flex items-center justify-between p-4 border-b">
        <h2 class="text-xl font-semibold text-gray-800">Detajet e Pagesës</h2>
        <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
          <i class="fa-solid fa-xmark fa-lg"></i>
        </button>
      </div>

      <div class="p-6">
        <div class="grid grid-cols-2 gap-4 mb-6">
          <div class="p-3 rounded-lg bg-blue-50">
            <div class="text-sm text-blue-800">Shuma Totale</div>
            <div class="text-2xl font-bold text-blue-900">{{ formatCurrency(totalAmount) }}</div>
          </div>
          <div class="p-3 rounded-lg" :class="remainingAmount === 0 ? 'bg-green-50' : 'bg-red-50'">
            <div class="text-sm" :class="remainingAmount === 0 ? 'text-green-800' : 'text-red-800'">Shuma e Mbetur</div>
            <div class="text-2xl font-bold" :class="remainingAmount === 0 ? 'text-green-900' : 'text-red-900'">{{ formatCurrency(remainingAmount) }}</div>
          </div>
        </div>

        <div class="space-y-3">
          <div v-for="(payment, index) in payments" :key="index" class="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
            <select v-model="payment.method_guid" class="flex-1 px-2 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option :value="null" disabled>Zgjidhni Metodën</option>
              <option v-for="method in getAvailableMethods(index)" :key="method.guid" :value="method.guid">
                {{ method.name }}
              </option>
            </select>

            <select v-model="payment.term_guid" class="flex-1 px-2 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option :value="null" disabled>Zgjidhni Këstin</option>
              <option v-for="term in processStore.payment_terms" :key="term.guid" :value="term.guid">
                {{ term.name }}
              </option>
            </select>

            <div class="relative flex-1">
              <input
                type="number"
                min="0"
                v-model.number="payment.amount"
                @input="handleAmountInput(payment)"
                placeholder="Shuma"
                class="w-full px-3 py-2 pr-8 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 no-spinner"
              />
              <i v-if="payment.amount > 0" @click="payment.amount = 0" class="absolute text-gray-400 cursor-pointer fa-solid fa-circle-xmark right-1 hover:text-gray-600"></i>
            </div>

            <div class="flex items-center gap-1">
              <button @click="fillMaxAmount(index)" class="flex-shrink-0 px-3 py-2 text-xs font-bold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">MAX</button>
              <button @click="removePayment(index)" v-if="payments.length > 1" class="flex-shrink-0 px-3 py-2 text-red-500 rounded-md hover:bg-red-100">
                <i class="fa-solid fa-trash-can"></i>
              </button>
            </div>
          </div>
        </div>

        <button @click="addPayment" class="w-full py-2 mt-4 text-sm text-blue-600 transition-colors border-2 border-dashed rounded-lg hover:bg-blue-50"><i class="mr-2 fa-solid fa-plus"></i>Shto Mënyrë Pagese</button>
      </div>

      <div class="flex justify-end gap-3 p-4 rounded-b-lg bg-gray-50">
        <button @click="closeModal" class="px-4 py-2 text-gray-800 bg-gray-200 rounded-lg hover:bg-gray-300">Anulo</button>
        <button
          @click="confirmPayment"
          :disabled="remainingAmount !== 0 || isConfirming"
          class="flex items-center gap-2 px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          <i v-if="isConfirming" class="fa fa-spinner fa-spin"></i>
          Konfirmo Pagesën
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useProcessStore } from "@/stores/process";
import { defineProps, defineEmits } from "vue";

const props = defineProps({
  totalAmount: { type: Number, required: true, default: 0 },
});
const emit = defineEmits(["close", "confirm"]);

const isConfirming = ref(false);
const payments = ref([{ method_guid: null, amount: 0, term_guid: null }]);
const processStore = useProcessStore();

const paidAmount = computed(() => {
  return payments.value.reduce((total, payment) => total + (Number(payment.amount) || 0), 0);
});

const remainingAmount = computed(() => {
  const remaining = props.totalAmount - paidAmount.value;
  return parseFloat(remaining.toFixed(2));
});

function getAvailableMethods(rowIndex) {
  const selectedGuids = payments.value.map((p) => p.method_guid).filter((guid) => guid !== null);
  const currentMethodGuid = payments.value[rowIndex].method_guid;
  return processStore.payment_methods.filter((method) => {
    return !selectedGuids.includes(method.guid) || method.guid === currentMethodGuid;
  });
}

// REMOVED: The conditional logic functions ('methodAllowsInstallments' and 'onMethodChange') are no longer needed.

function addPayment() {
  const newPayment = { method_guid: null, amount: 0, term_guid: null };
  // Pre-select the first payment term for the new row if available
  if (processStore.payment_terms && processStore.payment_terms.length > 0) {
    newPayment.term_guid = processStore.payment_terms[0].guid;
  }
  payments.value.push(newPayment);
}

function removePayment(index) {
  payments.value.splice(index, 1);
}

function closeModal() {
  emit("close");
}

function confirmPayment() {
  if (remainingAmount.value !== 0) {
    alert("Shuma e mbetur duhet te jete 0.");
    return;
  }
  for (const p of payments.value) {
    // CHANGE: Simplified validation. Now checks for all required fields on every line.
    if (!p.method_guid || !p.term_guid || p.amount <= 0) {
      alert("Ju lutem plotësoni të gjitha fushat (Metodën, Këstin, Shumën) për çdo pagesë.");
      return;
    }
  }
  isConfirming.value = true;
  emit("confirm", payments.value);
}

function fillMaxAmount(index) {
  const currentAmount = payments.value[index].amount || 0;
  const newAmount = currentAmount + remainingAmount.value;
  payments.value[index].amount = parseFloat(newAmount.toFixed(2));
}

function handleAmountInput(payment) {
  if (payment.amount < 0 || payment.amount === null) {
    payment.amount = 0;
  }
}

function formatCurrency(value) {
  if (typeof value !== "number") return "0.00 Lek";
  return `${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Lek`;
}

// Automatically select the first method and term for the initial row
if (processStore.payment_methods && processStore.payment_methods.length > 0) {
  payments.value[0].method_guid = processStore.payment_methods[0].guid;
}
if (processStore.payment_terms && processStore.payment_terms.length > 0) {
  payments.value[0].term_guid = processStore.payment_terms[0].guid;
}
</script>

<style scoped>
/* Styles for hiding number input spinners */
.no-spinner::-webkit-outer-spin-button,
.no-spinner::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.no-spinner {
  -moz-appearance: textfield;
}
</style>
